import { setSASApplicationNameHeader } from '../axios/interceptors/sas-app-name-header';
import { deferred } from '../util';
import { StaticConfigServiceAdapter } from './ConfigServiceAdapter';
/**
 * By default, configuration will create a scope. Consumers are not expected to call this as part of environment setup.
 * The main purpose of this is to isolate configuration for unit tests.
 * @returns
 */
export const createConfigScope = () => {
  const config = {
    get: function (name) {
      return config[name.toLowerCase()];
    }
  };
  let configPromise;
  const setValue = (name, value) => {
    if (value === undefined || value === null) {
      return;
    }
    // normalize property case.
    name = name.toLowerCase();
    switch (typeof value) {
      case 'boolean':
      case 'string':
      case 'number':
      case 'function':
        config[name] = value;
        break;
      case 'object':
        if (Array.isArray(value)) {
          config[name] = value;
        } else {
          config[name] = value;
          // TODO Review: I think we are trying to simplify nested property access.
          // Should we refrain from doing so? Consumer supplying an object can access
          // the property as object.
          let k;
          for (k in value) {
            setValue(`${name}.${k}`, value[k]);
          }
        }
        break;
      default:
        throw new Error('illegal state');
    }
  };
  const applySettingsFromJson = configJson => {
    let k;
    for (k in configJson) {
      setValue(k, configJson[k]);
    }
  };
  const applySettingsFromService = async (configJson, service) => {
    try {
      const configData = await service.getConfiguration(configJson);
      let k;
      for (k in configData) {
        setValue(k, configData[k]);
      }
    } catch (error) {
      // eslint-disable-next-line @sas/i18n/jsx-to-localize
      console.error(`Fetch configurations failed -${error}`);
      // still attempt to populate config from other sources
    }
  };

  const applySettingsFromUrl = () => {
    // apply the settings from the url
    const urlPrefix = 'nova-';
    const uriParams = new URLSearchParams(window.location.href);
    if (uriParams) {
      // now analyze nova- parameters
      for (const n in config) {
        const value = uriParams.get(urlPrefix + n);
        if (value === '' || `${value}` === '') {
          // empty URL parameters set the parameter back to its system default
          config[n] = undefined;
        } else {
          // sets the value (null or empty value ignored)
          setValue(n, value);
        }
      }
    }
  };
  async function internalInitConfig(configJson, service) {
    if (configPromise) return configPromise;
    service = service ?? new StaticConfigServiceAdapter({});
    const {
      promise,
      resolve
    } = deferred();
    configPromise = promise;
    applySettingsFromJson(configJson);
    await applySettingsFromService(configJson, service);
    setSASApplicationNameHeader(config.get('appName'));
    applySettingsFromUrl();
    resolve(config);
    return configPromise;
  }
  async function internalGetConfig() {
    return internalInitConfig({});
  }
  const internalGetConfigurationSync = () => {
    return config;
  };
  getConfiguration = internalGetConfig;
  initConfiguration = internalInitConfig;
  getConfigurationSync = internalGetConfigurationSync;
  const scope = {
    getConfiguration: internalGetConfig,
    initConfiguration: internalInitConfig,
    getConfigurationSync: internalGetConfigurationSync
  };
  return scope;
};
export let getConfiguration = () => {
  return createConfigScope().getConfiguration();
};
export let initConfiguration = (configJson, service) => {
  return createConfigScope().initConfiguration(configJson, service);
};
export let getConfigurationSync = () => {
  return createConfigScope().getConfigurationSync();
};
//# sourceMappingURL=Configuration.js.map